import Vue from 'vue'
import { i18n } from 'app/config'
import builderStore from '../builder/index'
import authStore from '../auth/index'
import notificationStore from '../notification'
import log from 'app/log.js'
import hasKeyInObject from 'core/utils/hasKeyInObject'
import searchKeyEqualToValueInObject from 'core/utils/searchKeyEqualToValueInObject'
import sanitizeTextComponents from 'core/utils/sanitizeTextComponents'
import { EARLY_RENEWAL_COMPONENT_NAME } from 'core/components/EarlyRenewal/consts'
import { categories } from 'app/core/constants.js'

export default new Vue({
  functional: true,
  i18n,
  data: () => ({
    apiUrl: process.env.COMPONENT_API,
    apiAppUrl: process.env.APP_API,
    usedImages: [],
    apiCheckoutBuilder: process.env.API_CHECKOUT_BUILDER,
    userPath: '/hot-component-layout-web/api/v1/component/user/holding',
    productPath: '/hot-component-layout-web/api/v3/component/product',
    coproductionProductPath: '/hot-component-layout-web/api/v1/component/coproduction/physicalProduct',
    featureFlagPath: '/hot-component-layout-web/api/v1/featureFlag'
  }),
  methods: {
    forEachComponent (rows, fn) {
      rows.forEach(row => row.columns.forEach(column => column.components.forEach(fn)))
    },
    getUsedImagesSet (template) {
      let usedImagesSet = new Set()
      if (template.backgroundImage.fileName) {
        usedImagesSet.add(template.backgroundImage.fileName)
      }

      if (template.slotComponents && template.slotComponents['slots-exit-popup']) {
        const { full, image } = template.slotComponents['slots-exit-popup'].data
        if (full && full.image.file !== null) {
          usedImagesSet.add(full.image.fileName)
        }
        if (image && image.exitImage.file !== null) {
          usedImagesSet.add(image.exitImage.fileName)
        }
      }
      this.forEachComponent(template.rows, component => {
        if (component.name.includes('image') || component.name.includes('review')) {
          usedImagesSet.add(component.data.image.fileName)
        } else if (component.name.includes('header')) {
          usedImagesSet.add(component.data.frontImage.fileName)
          usedImagesSet.add(component.data.mainImage.fileName)
        }
      })
      return usedImagesSet
    },
    clearPayloadJunk (template) {
      if (template.backgroundImage) {
        delete template.backgroundImage.file
      }

      if (Object.keys(template.slotComponents).length > 0 && typeof template.slotComponents['slots-exit-popup'] !== 'undefined') {
        const { full, image } = template.slotComponents['slots-exit-popup'].data

        if (full && full.image.file !== null) {
          delete template.slotComponents['slots-exit-popup'].data.full.image.file
        }

        if (image && image.exitImage.file !== null) {
          delete template.slotComponents['slots-exit-popup'].data.image.exitImage.file
        }
      }

      this.forEachComponent(template.rows, component => {
        if (component.name.includes('image')) {
          delete component.data.url
          delete component.data.image.file
        } else if (component.name.includes('review')) {
          delete component.data.image.file
        } else if (component.name.includes('header')) {
          delete component.data.frontImage.file
          delete component.data.mainImage.file

          if (typeof component.data.image !== 'undefined' && component.data.image !== null) {
            delete component.data.image
          }

          if (typeof component.data.banner !== 'undefined' && component.data.banner !== null) {
            delete component.data.banner
          }
        }
      })

      return template
    },
    uploadImages (images, checkoutId) {
      const processedImages = []

      for (let image in images) {
        if (images.hasOwnProperty(image)) {
          processedImages.push(images[image])
        }
      }

      const config = {
        timeout: 25000,
        onUploadProgress: (progressEvent) => {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total)
          builderStore.mutations.SET_BUILDER_LOADING({
            show: true,
            message: this.$t('builder.uploadingImages', { modeName: '', percent })
          })
        }
      }
      return processedImages.map((image) =>
        this.$http.post(this.apiUrl + 'file/upload', [image], config)
      )
    },
    async getParsedData (mode, language, checkout, uploadedImages, bypassUpload) {
      checkout.inCheckoutComponents = this.mapOrderBumpCustomImage(uploadedImages, checkout.inCheckoutComponents)
      // Only hotpay-text holds HTML; hotpay-title/subtitle are plain text and must stay untouched
      sanitizeTextComponents(checkout.rows)
      let payload = JSON.stringify({
        ...(!bypassUpload ? this.clearPayloadJunk(checkout) : checkout),
        payloadFileList: undefined // overwrites checkout's payloadFileList
      })

      if (payload) {
        // Replaces old removed components (hotpay-title and hoptpay-subtitle) to new unified hotpay-text
        payload = payload.replaceAll('hotpay-title', 'hotpay-text').replaceAll('hotpay-subtitle', 'hotpay-text')
      }

      let imageFileList = uploadedImages
      if (builderStore.state.currentImages != null && Object.keys(builderStore.state.currentImages).length > 0) {
        imageFileList = [
          ...JSON.parse(JSON.stringify(builderStore.state.currentImages)),
          ...imageFileList
        ]
      }
      const usedImagesSet = this.getUsedImagesSet(checkout)

      const checkoutMode = mode === 'desktop' ? 0 : 1
      console.group(mode)
      const payloadFileList = this.filterDuplicatedAndUnusedImages(usedImagesSet, imageFileList)
      console.table(payloadFileList)
      console.groupEnd()
      return Promise.resolve({
        id: checkout.editMode,
        locale: language.toUpperCase().replace('-', '_'),
        isPublished: true,
        payloadFileList,
        checkoutMode,
        payload
      })
    },
    mapOrderBumpCustomImage (uploadedImages, components) {
      return components.map(component => {
        const { customImage } = component?.data || {}
        const isOrderBump = component.name === 'hotpay-order-bump'
        const matchingImage = uploadedImages.find(image => image.field === customImage?.fileName)

        if (isOrderBump && matchingImage?.componentLayoutFileResponseVO?.webPath) {
          component.data.customImage.source = matchingImage.componentLayoutFileResponseVO.webPath
        }
        return component
      })
    },
    filterDuplicatedAndUnusedImages (usedImagesSet, imagesList) {
      const bufferSet = new Set()
      return imagesList.filter(image => {
        const filename = image.field
        const isUsed = usedImagesSet.has(filename)
        const isUnique = !bufferSet.has(filename)
        if (isUnique) bufferSet.add(filename)
        return isUsed && isUnique
      })
    },
    setEarlyRenewalCouponTemplate ({ templatesToInsertCoupon, coupon }) {
      const keyToSearch = 'name'
      const equalsToValue = EARLY_RENEWAL_COMPONENT_NAME

      Object.keys(templatesToInsertCoupon).forEach((key) => {
        if (templatesToInsertCoupon[key] && typeof templatesToInsertCoupon[key] === 'object') {
          if (Object.prototype.hasOwnProperty.call(templatesToInsertCoupon[key], keyToSearch)) {
            const component = templatesToInsertCoupon[key]

            if (component[keyToSearch] === equalsToValue) {
              component.data.product = {
                ...component.data.product,
                coupon
              }
            }
          }

          this.setEarlyRenewalCouponTemplate({
            templatesToInsertCoupon: templatesToInsertCoupon[key],
            coupon
          })
        }
      })

      return templatesToInsertCoupon
    },
    async normalizeEarlyRenewalTemplate ({ templates, currentProductEarlyRenewal }) {
      let newTemplates = JSON.parse(JSON.stringify(templates))
      const hasHotpayEarlyRenewal = hasKeyInObject(EARLY_RENEWAL_COMPONENT_NAME, templates)

      if (hasHotpayEarlyRenewal) {
        const coupon = await this.getEarlyRenewalDiscountCoupons({
          templates,
          currentProductEarlyRenewal
        })

        newTemplates = this.setEarlyRenewalCouponTemplate({
          templatesToInsertCoupon: newTemplates,
          coupon
        })
      }

      return newTemplates
    },
    async getPayloadData (data, bypassUpload, isTemplate) {
      const {
        templates,
        imageList,
        currentProduct,
        checkoutName,
        checkoutDescription,
        checkoutId,
        editMode,
        templateId
      } = data
      const checkoutNames = Object.keys(templates)
      let payloadList = []
      let checkoutCount = checkoutNames.length

      let uploadedImages = []
      if (Object.keys(imageList).length) {
        const imagesToUpload = this.uploadImages(imageList, checkoutId)

        await Promise.all(imagesToUpload).then((result) => {
          const images = result.map(imageUploadResponse => imageUploadResponse.data[0])
          builderStore.mutations.SET_BUILDER_LOADING({
            show: true,
            message: this.$t('builder.finishingSave')
          })
          uploadedImages = images
        }).catch((error) => {
          if (error.message.includes('timeout')) {
            log({
              currentProduct,
              checkoutId,
              templateId,
              editMode,
              imageList: Object.keys(imageList).map(key => ({ ...imageList[key], imageBase64: 'erased' }))
            })
          }
          notificationStore.actions.SHOW_NOTIFICATION([this.$t('builder.defaultError'), 'error'])

          builderStore.mutations.SET_BUILDER_LOADING({
            show: false
          })
          throw new Error(error)
        })
      }

      while (checkoutCount--) {
        const name = checkoutNames[checkoutCount]
        const matches = /(\w+)+-((\w+)+-?(\w+)?)/.exec(name)

        if (matches) {
          const [, mode, language] = matches
          let singleCheckout = JSON.parse(JSON.stringify(templates[name]))

          if (mode === 'desktop' && singleCheckout.rows.length === 0) {
            builderStore.actions.COPY_MOBILE_CHECKOUT_TO_DESKTOP()
            singleCheckout = JSON.parse(JSON.stringify(templates[name]))
          }

          const payload = await this.getParsedData(mode, language, singleCheckout, uploadedImages, bypassUpload, isTemplate, checkoutId)

          payloadList.push(payload)
        }
      }

      let checkoutData = {
        idProduct: +currentProduct,
        idOffer: 1,
        name: checkoutName,
        payloadList
      }

      if (checkoutDescription) {
        checkoutData.description = checkoutDescription
      }

      if (!isTemplate) {
        checkoutData.idTemplateOrigin = templateId
      }

      if (editMode) {
        checkoutData.id = !isTemplate ? +checkoutId : +templateId
        checkoutData['payloadList'].forEach(payload => {
          payload.editMode = true
        })
      }

      return checkoutData
    },
    saveCheckout: async function (data) {
      data.templates = await this.normalizeEarlyRenewalTemplate({
        templates: data.templates,
        currentProductEarlyRenewal: data.currentProductEarlyRenewal
      })

      const checkoutData = await this.getPayloadData(data)

      if (data.editMode) {
        this.$analytics('send', {
          hitType: 'event',
          eventCategory: 'Build Checkout',
          eventAction: 'save',
          eventLabel: 'Edit Checkout'
        })

        return Promise.resolve(await this.$http.put(this.apiUrl + 'checkoutLayout', checkoutData))
      }

      this.$analytics('send', {
        hitType: 'event',
        eventCategory: 'Build Checkout',
        eventAction: 'save',
        eventLabel: 'Create Checkout'
      })

      return Promise.resolve(await this.$http.post(this.apiUrl + 'checkoutLayout', checkoutData))
    },
    async saveTemplate (data, bypassParse) {
      let checkoutData = data

      if (!bypassParse) {
        checkoutData = await this.getPayloadData(data, null, true)
      }

      if (data.editMode) {
        return Promise.resolve(await this.$http.put(this.apiUrl + 'templateLayout', checkoutData))
      }

      return Promise.resolve(await this.$http.post(this.apiUrl + 'templateLayout', checkoutData))
    },
    async cloneCheckout (checkoutData) {
      return Promise.resolve(await this.$http.post(this.apiUrl + 'checkoutLayout', checkoutData))
    },
    async cloneCheckoutFromOtherProducts (checkoutData) {
      return Promise.resolve(await this.$http.post(`${this.apiUrl}duplicate/checkoutLayout`, checkoutData))
    },
    getTemplate (templateId) {
      return this.$http.get(this.apiUrl + 'templateLayout/' + templateId)
    },
    getProductTemplate (templateId, productId) {
      return this.$http.get(`${this.apiUrl}templateLayout/${templateId}/${productId}`)
    },
    getCheckout (checkoutId) {
      if (checkoutId) { return this.$http.get(this.apiUrl + 'checkoutLayout/' + checkoutId) }
    },
    async getDiscountCoupons () {
      const { data } = await this.$http.get(this.apiUrl + 'discountCoupons/' + authStore.state.currentProduct)
      return data
    },
    async getEarlyRenewalDiscountCoupons ({ templates, currentProductEarlyRenewal }) {
      let coupon = currentProductEarlyRenewal?.coupon

      if (!coupon?.id) {
        const hotpayEarlyRenewal = searchKeyEqualToValueInObject({
          keyToSearch: 'name',
          equalsToValue: EARLY_RENEWAL_COMPONENT_NAME,
          objectToSearch: templates
        })

        const hotpayEarlyRenewalData = hotpayEarlyRenewal.find(Boolean).data
        const { product, proposal } = hotpayEarlyRenewalData

        const { data } = await this.$http.post(this.apiAppUrl + 'marketplace/rest/v1/subscriptionAnticipation/orderbump/' + product.productId, {
          offerIds: [product.id],
          discount: proposal.discountPercentage / 100,
          renewalsNumber: proposal.recurrences
        })

        coupon = {
          id: data.couponId,
          code: data.couponCode
        }
      }

      return coupon
    },
    async getProductsWithDetails () {
      const { data } = await this.$http.get(this.apiUrl + 'productOfferDetail')
      return data
    },
    async getHoldingUsers () {
      const { data } = await this.$http.get(`${this.apiCheckoutBuilder}${this.userPath}`)
      return data
    },
    getHoldingFilter (holdingUserId) {
      return holdingUserId ? `/holding/${holdingUserId}` : ''
    },
    formatProductsResponse (response) {
      const { data: { data: productDataList, page: currentPage, size: pageSize } } = response

      const products = (productDataList ?? []).map(product => {
        return {
          ...product,
          categoryId: product.categoryId,
          category: categories[product.categoryId]
        }
      })

      return { products, currentPage, pageSize }
    },
    async getProducts (page, rowsPerPage, holdingUserId) {
      const apiUrl = `${this.getHoldingFilter(holdingUserId)}?page=${page}&rows=${rowsPerPage}`

      const response = await this.$http.get(`${this.apiCheckoutBuilder}${this.productPath}${apiUrl}`)

      return this.formatProductsResponse(response)
    },
    async searchProducts (searchProductName, holdingUserId) {
      const response = await this.$http.get(`${this.apiCheckoutBuilder}${this.productPath}${this.getHoldingFilter(holdingUserId)}?name=${searchProductName}`)
      if (!response?.data) {
        return { products: [] }
      }

      return this.formatProductsResponse(response)
    },
    // --- Feature Flag ---
    async getFeatureFlag (name) {
      try {
        const { data } = await this.$http.get(`${this.apiCheckoutBuilder}${this.featureFlagPath}?name=${name}`)
        return data?.enabled ?? false
      } catch (error) {
        return false
      }
    },
    // --- Coprodução ---
    async getCoproductionProducts (page, rowsPerPage, name) {
      let url = `${this.apiCheckoutBuilder}${this.coproductionProductPath}?page=${page}&rows=${rowsPerPage}`
      if (name) {
        url += `&name=${encodeURIComponent(name)}`
      }
      const response = await this.$http.get(url)
      if (!response?.data) {
        return { products: [] }
      }
      return this.formatProductsResponse(response)
    }
  }
})
